import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import React, { useState } from 'react';
import { Loader, Mail, Plus, Trash2, User2 } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { CircleUser } from 'lucide-react';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { useDeleteClientUserMutation, useGetOrganizationClientsQuery, useResendClientUserInviteMutation } from 'src/pages/client-management/endpoints/client.endpoints';
import CreateClientUser from '../create-client-user';
import { showNotification } from 'src/redux/notification/notification.slice';
import { useDispatch } from 'react-redux';

interface ClientUserTableProps {
  className?: string;
}

const ClientUserTable: React.FC<ClientUserTableProps> = ({
  className = '',
}) => {
  const dispatch = useDispatch();

  const [showAddUser, setShowAddUser] = useState<boolean>(false);

  const { activeClient, setClient } = useActiveClient();

  const [disabledResends, setDisabledResends] = useState<string[]>([]);
  const { data: clients, refetch: refetchClientsList } = useGetOrganizationClientsQuery();

  const [resendClientUserInvite, { isLoading: isLoadingResendInvite }] = useResendClientUserInviteMutation();
  const [deleteClientUser, { isLoading: isLoadingDeleteUser }] = useDeleteClientUserMutation();

  const handleResendInvite = async(e: React.FormEvent, clientUserId: string) => {
    e.preventDefault();
    if (!clientUserId) {
      dispatch(showNotification({
        summary: 'No client user selected',
        theme: 'warning',
      }));
      return;
    }

    await resendClientUserInvite({
      clientUserId: clientUserId,
    });

    dispatch(showNotification({
      summary: 'Invite resent',
      theme: 'success',
    }));

    setDisabledResends([...disabledResends, clientUserId]);
  };

  const handleDeleteUser = async(e: React.FormEvent, clientUserId: string) => {
    e.preventDefault();
    if (!clientUserId) {
      dispatch(showNotification({
        summary: 'No client user selected',
        theme: 'warning',
      }));
      return;
    }

    const response = await deleteClientUser({
      clientUserId: clientUserId,
    });

    if (!('data' in response)) {
      dispatch(showNotification({
        summary: 'Error deleting user',
        theme: 'warning',
      }));
      return;
    }

    dispatch(showNotification({
      summary: 'User deleted',
      theme: 'success',
    }));

    const { data: clientsList } = await refetchClientsList();
    if (clientsList && activeClient?.ext_client_id) {
      const updatedClient = clientsList.find(
        (client) => client.ext_client_id === activeClient.ext_client_id
      );
      if (updatedClient) {
        setClient(updatedClient);
      }
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
    });
  };

  return (
    <div className='flex flex-row justify-between overflow-hidden p-4 pb-0'>
      <div className='w-full bg-white rounded-md overflow-hidden shadow-sm overflow-x-auto'>
        <div className='flex flex-row items-center bg-gray-201 font-medium text-black gap-2 pl-4 pb-0 justify-between'>
          <div className='flex flex-row items-center gap-2'>
            <CircleUser className='size-4' />
            Users
          </div>
          <div>
            <Button
              variant='ghost'
              size='sm'
              className='text-black'
              onClick={() => setShowAddUser(!showAddUser)}
            >
              <Plus className='size-4' />
            </Button>
          </div>
        </div>
        {showAddUser ? (
          <div className='p-4 border border-yellow-500 border-dashed border-2'>
            <CreateClientUser
              onCancel={() => setShowAddUser(false)}
            />
          </div>
        ) : null}
        <Table className='w-full min-w-full'>
          <TableHeader className='border-b border-gray-201'>
            <TableRow className='text-xs'>
              <TableHead className='w-1/4'>
                STATUS
              </TableHead>
              <TableHead className='w-1/4'>
                EMAIL
              </TableHead>
              <TableHead className='w-1/4 text-center'>
                ACTIONS
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeClient?.users.length === undefined || activeClient?.users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className='p-2 text-center'
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : (
              activeClient?.users.length && activeClient?.users.length > 0 ? (
                activeClient.users.map((user) => (
                  <TableRow
                    key={user.id}
                    className='border-b border-gray-201'
                  >
                    <TableCell className='p-2 border-r border-gray-201'>
                      <div className='flex flex-row items-center gap-2'>
                        {user.pending || !user.user ? <div className='size-2 rounded-full bg-blue-500' /> : <div className='size-2 rounded-full bg-green-501' />}
                        {user.pending || !user.user ? 'Invite Sent @ ' + formatDate(user.created_at) : 'Active'}
                      </div>

                    </TableCell>
                    <TableCell className='p-2 border-r border-gray-201'>
                      {user.email}
                    </TableCell>
                    <TableCell className='p-2'>
                      <div className='flex flex-row items-center justify-center mx-auto gap-2 w-fit'>
                        <Button
                          disabled={disabledResends.includes(user.id) || isLoadingResendInvite}
                          variant='outline'
                          size='sm'
                          className='text-black hover:bg-gray-200'
                          onClick={(e) => handleResendInvite(e, user.id)}
                        >
                          {isLoadingResendInvite ? <Loader className='size-4 animate-spin' /> : <Mail className='size-4' />}
                          Resend
                        </Button>
                        <Button
                          disabled={isLoadingDeleteUser}
                          variant='outline'
                          size='sm'
                          className='text-black hover:bg-gray-200'
                          onClick={(e) => handleDeleteUser(e, user.id)}
                        >
                          {isLoadingDeleteUser ? <Loader className='size-4 animate-spin' /> : <Trash2 className='size-4' />}
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className='p-8 font-bold items-center justify-center'
                  >
                    <div className='flex flex-col items-center justify-center'>
                      <User2 className='size-6 mr-1' />
                      No users found
                    </div>

                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ClientUserTable;
